import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, Input } from "@tellescope/design-system";
import { NumberInput, Button } from "@tellescope/ui";
import { Search, AlertCircle } from "lucide-react";
import { useUsdaFoodSearchQuery, type USDAFoodSummary } from "@tellescope/tanstack-query";
import { useForm } from "@tellescope/forms";
import type { EnduserObservation, ObservationComponent } from "@tellescope/types-models";

interface FoodEntrySectionProps {
  /** When set, the form skips USDA search and pre-fills the manual macro fields from this entry. */
  editingEntry?: EnduserObservation;
  onLog: (params: {
    totalCalories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
    items?: ObservationComponent[];
  }) => Promise<void>;
  onUpdate?: (params: {
    totalCalories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  }) => Promise<void>;
  isPending: boolean;
}

const round = (value: number): number => Math.round(value * 10) / 10;

const macroFromComponents = (entry: EnduserObservation | undefined, label: string): number | undefined =>
  entry?.components?.find((c) => c.code.text === label)?.valueQuantity.value;

export const FoodEntrySection = ({ editingEntry, onLog, onUpdate, isPending }: FoodEntrySectionProps) => {
  const { t } = useTranslation("portal");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState<{ keywords: string } | undefined>(undefined);
  const [selectedFood, setSelectedFood] = useState<USDAFoodSummary | undefined>(undefined);
  const [quantityGrams, setQuantityGrams] = useState<number | undefined>(100);

  const manualForm = useForm({
    fields: {
      calories: { type: "number", initialValue: editingEntry?.measurement.value ?? 0 },
      protein: { type: "number", initialValue: macroFromComponents(editingEntry, "Protein") ?? 0 },
      carbs: { type: "number", initialValue: macroFromComponents(editingEntry, "Carbohydrate") ?? 0 },
      fat: { type: "number", initialValue: macroFromComponents(editingEntry, "Fat") ?? 0 },
      fiber: { type: "number", initialValue: macroFromComponents(editingEntry, "Fiber") ?? 0 },
    },
  });
  const {
    calories: manualCalories,
    protein: manualProtein,
    carbs: manualCarbs,
    fat: manualFat,
    fiber: manualFiber,
  } = manualForm.values;

  // NumberInput's onChange takes the raw number directly (not a form field's onValueChange
  // wrapper name) — adapt getCustomFieldProps's shape to what NumberInput actually expects.
  const numberFieldProps = (field: "calories" | "protein" | "carbs" | "fat" | "fiber") => {
    const { onValueChange, ...rest } = manualForm.getCustomFieldProps(field);
    return { ...rest, onChange: (newValue: number | undefined) => onValueChange(newValue ?? 0) };
  };

  const { data, isFetching, isError, error } = useUsdaFoodSearchQuery({ query: searchQuery });

  const searchUnavailable =
    isError && error instanceof Error && error.message.includes("USDA food search is not configured");

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    if (trimmed.length < 2) return;
    setSearchQuery({ keywords: trimmed });
  };

  const handleSelectFood = (food: USDAFoodSummary) => {
    setSelectedFood(food);
    setQuantityGrams(100);
  };

  const scaledMacro = (per100g: number | undefined): number =>
    per100g === undefined || quantityGrams === undefined ? 0 : round((per100g * quantityGrams) / 100);

  const handleLogSearchedFood = async () => {
    if (!selectedFood || quantityGrams === undefined || quantityGrams <= 0) return;
    await onLog({
      totalCalories: scaledMacro(selectedFood.calories),
      protein: scaledMacro(selectedFood.protein),
      carbs: scaledMacro(selectedFood.carbs),
      fat: scaledMacro(selectedFood.fat),
      fiber: scaledMacro(selectedFood.fiber),
      items: [
        {
          code: {
            text: selectedFood.description,
            coding: [{ system: "https://fdc.nal.usda.gov", code: String(selectedFood.fdcId) }],
          },
          valueQuantity: { value: quantityGrams, unit: "g" },
        },
      ],
    });
    setSelectedFood(undefined);
    setQuantityGrams(100);
  };

  const handleSubmitManualEntry = async () => {
    if (manualCalories <= 0) return;
    const params = {
      totalCalories: manualCalories,
      protein: manualProtein,
      carbs: manualCarbs,
      fat: manualFat,
      fiber: manualFiber,
    };
    if (editingEntry) {
      await onUpdate?.(params);
      return;
    }
    await onLog(params);
    manualForm.resetForm();
  };

  if (editingEntry) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("Calories")}</Label>
            <NumberInput {...numberFieldProps("calories")} precisionOptions={{ maxDecimalDigitCount: 0 }} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Protein (g)")}</Label>
            <NumberInput {...numberFieldProps("protein")} precisionOptions={{ maxDecimalDigitCount: 1 }} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Carbs (g)")}</Label>
            <NumberInput {...numberFieldProps("carbs")} precisionOptions={{ maxDecimalDigitCount: 1 }} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Fat (g)")}</Label>
            <NumberInput {...numberFieldProps("fat")} precisionOptions={{ maxDecimalDigitCount: 1 }} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Fiber (g)")}</Label>
            <NumberInput {...numberFieldProps("fiber")} precisionOptions={{ maxDecimalDigitCount: 1 }} />
          </div>
        </div>
        <Button onClick={handleSubmitManualEntry} disabled={manualCalories <= 0 || isPending}>
          {t("Update reading")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>{t("Search for a food")}</Label>
        <div className="flex gap-2">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder={t("e.g. cheddar cheese")}
            data-test="food-search-input"
          />
          <Button
            onClick={handleSearch}
            disabled={searchInput.trim().length < 2 || isFetching}
            data-test="food-search-button"
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {isError && (
        <div className="rounded-md border border-dashed p-3 space-y-1">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <AlertCircle className="h-4 w-4" />
            {searchUnavailable ? t("Food search is currently unavailable") : t("Food search failed")}
          </div>
          <p className="text-xs text-muted-foreground">{t("You can enter your macros manually below instead.")}</p>
        </div>
      )}

      {data?.data.foods.length === 0 && !isError && (
        <p className="text-sm text-muted-foreground">
          {t("No results found. Try a different search or enter your macros manually below.")}
        </p>
      )}

      {data && data.data.foods.length > 0 && (
        <div className="space-y-1 max-h-48 overflow-y-auto rounded-md border p-1">
          {data.data.foods.map((food) => (
            <button
              key={food.fdcId}
              type="button"
              onClick={() => handleSelectFood(food)}
              className="w-full text-left rounded px-2 py-1.5 text-sm hover:bg-muted"
              data-test="food-search-result"
            >
              {food.description}
            </button>
          ))}
        </div>
      )}

      {selectedFood && (
        <div className="space-y-3 rounded-md border p-3">
          <p className="text-sm font-medium">{selectedFood.description}</p>
          <div className="space-y-1.5">
            <Label>{t("Quantity (grams)")}</Label>
            <NumberInput
              value={quantityGrams}
              onChange={setQuantityGrams}
              precisionOptions={{ maxDecimalDigitCount: 0 }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("{{calories}} kcal, {{protein}}g protein, {{carbs}}g carbs, {{fat}}g fat, {{fiber}}g fiber", {
              calories: scaledMacro(selectedFood.calories),
              protein: scaledMacro(selectedFood.protein),
              carbs: scaledMacro(selectedFood.carbs),
              fat: scaledMacro(selectedFood.fat),
              fiber: scaledMacro(selectedFood.fiber),
            })}
          </p>
          <Button onClick={handleLogSearchedFood} disabled={isPending}>
            {t("Add to log")}
          </Button>
        </div>
      )}

      <div className="pt-2 border-t space-y-3">
        <p className="text-sm font-medium">{t("Or enter macros manually")}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("Calories")}</Label>
            <NumberInput {...numberFieldProps("calories")} precisionOptions={{ maxDecimalDigitCount: 0 }} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Protein (g)")}</Label>
            <NumberInput {...numberFieldProps("protein")} precisionOptions={{ maxDecimalDigitCount: 1 }} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Carbs (g)")}</Label>
            <NumberInput {...numberFieldProps("carbs")} precisionOptions={{ maxDecimalDigitCount: 1 }} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Fat (g)")}</Label>
            <NumberInput {...numberFieldProps("fat")} precisionOptions={{ maxDecimalDigitCount: 1 }} />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Fiber (g)")}</Label>
            <NumberInput {...numberFieldProps("fiber")} precisionOptions={{ maxDecimalDigitCount: 1 }} />
          </div>
        </div>
        <Button onClick={handleSubmitManualEntry} disabled={manualCalories <= 0 || isPending}>
          {t("Add to log")}
        </Button>
      </div>
    </div>
  );
};
